import React, { useState, useEffect } from 'react';
import { Upload, Sparkles, MessageSquare, CreditCard, Upload as UploadIcon, Brain, Mic, Chrome } from 'lucide-react';
import uploadScreenshot from '../assets/images/upload-post-screenshot.jpg';
import questionScreenshot from '../assets/images/question-screenshot.jpg';
import analysisStyleScreenshot from '../assets/images/analysis-style-screenshot.jpg';
import postContextScreenshot from '../assets/images/post-context-screenshot.jpg';
import linkedinOutputScreenshot from '../assets/images/linkedin-post-output-screenshot.jpg';
import recordResponseScreenshot from '../assets/images/record-response-screenshot.jpg';
import profilePageScreenshot from '../assets/images/profile-page.jpg';
import chromeExtensionStyles from '../assets/images/chrome-extension-styles.jpg';
import chromeExtensionLinkedin from '../assets/images/chrome-extension-linkedin.jpg';
import logo from '../assets/images/logo-300x300.png';
import AuthButton from './AuthButton';
import SignUpButton from './SignUpButton';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import postnudgeVideo from '../assets/images/postnudge-video.gif';

const CreditsDisplay = ({ credits }: { credits: number | 'unlimited' }) => {
  if (credits === 'unlimited') {
    return (
      <div className="text-sm text-gray-600">
        Pro Plan - Unlimited Credits
      </div>
    );
  }

  return (
    <div className="text-sm text-gray-600">
      {credits} credit{credits !== 1 ? 's' : ''} remaining
      {credits <= 1 && (
        <span className="text-yellow-600 ml-2">
          ⚠️ Running low on credits
        </span>
      )}
    </div>
  );
};

const SubscriptionModal = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
  const paymentLinks = {
    basic: 'https://buy.stripe.com/test_bIY15mdFT0Wh5ageUU',
    pro: 'https://buy.stripe.com/test_bIYeWcdFTfRb0U03cd'
  };
  
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-md w-full p-6">
        <h2 className="text-2xl font-bold mb-4">
          You've Used All Your Free Credits!
        </h2>
        <p className="mb-6">
          Choose a plan to continue generating amazing LinkedIn posts:
        </p>
        
        <div className="space-y-4">
          <a 
            href={paymentLinks.basic}
            className="block w-full p-4 text-center bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Basic Plan - $9.99/month
            <span className="block text-sm">50 posts per month</span>
          </a>
          
          <a 
            href={paymentLinks.pro}
            className="block w-full p-4 text-center bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            Pro Plan - $19.99/month
            <span className="block text-sm">Unlimited posts</span>
          </a>
        </div>

        <button 
          onClick={onClose}
          className="mt-4 w-full p-2 text-gray-600 hover:text-gray-800"
        >
          Maybe later
        </button>
      </div>
    </div>
  );
};

const getExtensionId = async () => {
  try {
    window.postMessage({ 
      type: 'POSTNUDGE_REQUEST_EXTENSION_ID',
      timestamp: Date.now()
    }, window.location.origin);
    const response = await new Promise((resolve) => {
      const handleExtensionConnect = (event: MessageEvent) => {
        if (event.data && event.data.type === 'POSTNUDGE_EXTENSION_CONNECT') {
          resolve(event.data.extensionId);
        }
      };
      window.addEventListener('message', handleExtensionConnect);
      setTimeout(() => {
        window.removeEventListener('message', handleExtensionConnect);
        resolve(null);
      }, 1000);
    });
    return response;
  } catch (error) {
    return null;
  }
};

export default function LandingPage() {
  const [showSubscribeModal, setShowSubscribeModal] = useState(false);
  const [credits, setCredits] = useState<number | 'unlimited'>(3); // Start with 3 free credits
  const [generatedPost, setGeneratedPost] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const oldId = localStorage.getItem('postnudgeExtensionId');
    if (oldId === 'cnebnhncpobjmlegjlfhlglmamflnlko') {
      localStorage.removeItem('postnudgeExtensionId');
    }

    getExtensionId();
  }, []);

  useEffect(() => {
    const handleExtensionConnect = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) {
        return;
      }

      const { data } = event;
      if (data && data.type === 'POSTNUDGE_EXTENSION_CONNECT') {
        if (data.extensionId) {
          localStorage.setItem('postnudgeExtensionId', data.extensionId);
        }
      }
    };

    window.addEventListener('message', handleExtensionConnect);
    return () => {
      window.removeEventListener('message', handleExtensionConnect);
    };
  }, []);

  const handleGenerate = async () => {
    setIsGenerating(true);
    setError('');

    try {
      const response = await fetch('/api/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt: promptText })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to generate post');
      }

      if (data.needs_subscription) {
        setShowSubscribeModal(true);
      } else {
        setGeneratedPost(data.post);
        if (data.credits_remaining !== 'unlimited') {
          setCredits(data.credits_remaining);
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSignOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      // Clear any local state/storage if needed
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="space-y-24">
          {/* Hero Section */}
          <div className="text-center">
            <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-6xl">
              Create LinkedIn Posts With Your Voice
            </h1>
            <p className="mt-6 text-lg leading-8 text-gray-600">
              Just start talking. PostNudge turns your spoken thoughts into polished LinkedIn posts that engage your network and build your brand.
            </p>

            {/* Video Demo - Smaller size */}
            <div className="mt-8 rounded-xl bg-gray-50 p-2 sm:p-4 shadow-lg max-w-xl mx-auto">
              <div className="relative" style={{ paddingBottom: `${(720 / 872) * 100}%` }}>
                <img
                  src={postnudgeVideo}
                  alt="PostNudge Demo"
                  className="absolute inset-0 w-full h-full object-contain rounded-lg shadow-sm"
                />
              </div>
            </div>

            {/* Buttons below video with more padding */}
            <div className="mt-16 flex justify-center space-x-4">
              <SignUpButton 
                variant="hero"
                text="Try PostNudge For Free"
              />
              <a 
                href="https://chromewebstore.google.com/detail/postnudge-voice-to-linked/cnebnhncpobjmlegjlfhlglmamflnlko?authuser=0&hl=en&pli=1"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <Chrome className="mr-2 h-5 w-5" />
                Install Chrome Extension
              </a>
            </div>
          </div>

          {/* Chrome Extension Section */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Start With The Chrome Extension
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Click the PostNudge logo in LinkedIn's New Post window, select a post style, and start talking!
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div className="rounded-lg overflow-hidden shadow-lg">
                <img
                  src={chromeExtensionLinkedin}
                  alt="PostNudge Chrome Extension in LinkedIn"
                  className="w-full h-auto"
                />
                <div className="p-4 bg-white">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Seamless LinkedIn Integration
                  </h3>
                  <p className="text-gray-600">
                    Click the PostNudge logo to record directly in LinkedIn's composer
                  </p>
                </div>
              </div>
              
              <div className="rounded-lg overflow-hidden shadow-lg">
                <img
                  src={chromeExtensionStyles}
                  alt="PostNudge Post Styles"
                  className="w-full h-auto"
                />
                <div className="p-4 bg-white">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Choose Your Post Style
                  </h3>
                  <p className="text-gray-600">
                    Select from multiple styles to give your post a different angle
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Why PostNudge */}
          <div className="space-y-8">
            <h2 className="text-3xl font-bold text-center text-gray-900">Why PostNudge?</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center space-y-4">
                <div className="bg-blue-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto">
                  <MessageSquare className="w-6 h-6 text-blue-600" />
                </div>
                <h4 className="font-semibold text-gray-900">Break Through Writer's Block</h4>
                <p className="text-gray-600">Starting from a blank page is daunting. Skip the content calendar and let your natural inspiration guide your posts.</p>
              </div>
              <div className="text-center space-y-4">
                <div className="bg-purple-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto">
                  <Mic className="w-6 h-6 text-purple-600" />
                </div>
                <h4 className="font-semibold text-gray-900">Think Like You're On a Podcast</h4>
                <p className="text-gray-600">When someone asks you the right question, sharing your expertise feels effortless. We recreate that natural flow.</p>
              </div>
              <div className="text-center space-y-4">
                <div className="bg-green-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto">
                  <Sparkles className="w-6 h-6 text-green-600" />
                </div>
                <h4 className="font-semibold text-gray-900">Keep Your Unique Voice</h4>
                <p className="text-gray-600">Most AI strips away personality. By starting with your voice, we preserve what makes your content authentically you.</p>
              </div>
            </div>
          </div>

          {/* Features Section */}
          <div className="space-y-16">
            <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">Answer Questions To Create Content</h2>
            
            {/* Feature 1: Get Inspired */}
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="space-y-6">
                <div className="bg-blue-100 w-12 h-12 rounded-full flex items-center justify-center">
                  <UploadIcon className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Start with Inspiration</h3>
                <p className="text-lg text-gray-600">
                  Found a LinkedIn post that resonates? Upload it and let our AI analyze what makes it engaging. We'll help you create content that stands out while staying true to your voice.
                </p>
              </div>
              <div className="rounded-xl overflow-hidden shadow-xl">
                <img src={uploadScreenshot} alt="Upload Interface" className="w-full" />
              </div>
            </div>

            {/* Feature 2: AI Analysis */}
            <div className="grid md:grid-cols-2 gap-12 items-center md:flex-row-reverse">
              <div className="space-y-6 md:order-2">
                <div className="bg-purple-100 w-12 h-12 rounded-full flex items-center justify-center">
                  <Brain className="w-6 h-6 text-purple-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Get Thought-Provoking Prompts</h3>
                <p className="text-lg text-gray-600">
                  Our AI helps you explore different angles and perspectives for your content, ensuring each post adds value to your professional network.
                </p>
              </div>
              <div className="rounded-xl overflow-hidden shadow-xl md:order-1">
                <img src={questionScreenshot} alt="AI Analysis Interface" className="w-full" />
              </div>
            </div>

            {/* Feature 3: Personalized Profile */}
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="space-y-6">
                <div className="bg-green-100 w-12 h-12 rounded-full flex items-center justify-center">
                  <CreditCard className="w-6 h-6 text-green-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Tailored to Your Brand</h3>
                <p className="text-lg text-gray-600">
                  Tell us about yourself, your company, and the problems you solve. We'll craft content that perfectly aligns with your professional identity and business goals.
                </p>
              </div>
              <div className="rounded-xl overflow-hidden shadow-xl">
                <img src={profilePageScreenshot} alt="Profile Customization Interface" className="w-full" />
              </div>
            </div>

            {/* Feature 4: Voice Recording */}
            <div className="grid md:grid-cols-2 gap-12 items-center md:flex-row-reverse">
              <div className="space-y-6 md:order-2">
                <div className="bg-yellow-100 w-12 h-12 rounded-full flex items-center justify-center">
                  <Mic className="w-6 h-6 text-yellow-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Ramble On</h3>
                <p className="text-lg text-gray-600">
                  No more writer's block. Just talk naturally about your insights and experiences. Our AI will transform your voice into a polished, engaging LinkedIn post that sounds authentically you.
                </p>
              </div>
              <div className="rounded-xl overflow-hidden shadow-xl md:order-1">
                <img src={recordResponseScreenshot} alt="Voice Recording Interface" className="w-full" />
              </div>
            </div>

            {/* Feature 5: AI Magic */}
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="space-y-6">
                <div className="bg-blue-100 w-12 h-12 rounded-full flex items-center justify-center">
                  <Sparkles className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Perfect Your Post</h3>
                <p className="text-lg text-gray-600">
                  We'll format your insights to look natural on LinkedIn while maintaining your professional voice. Add hooks, emojis, and perfect spacing - all optimized for your audience.
                </p>
              </div>
              <div className="rounded-xl overflow-hidden shadow-xl">
                <img src={linkedinOutputScreenshot} alt="Final Post Interface" className="w-full" />
              </div>
            </div>
          </div>

          {/* Pricing Section */}
          <div className="space-y-8">
            <h2 className="text-3xl font-bold text-center text-gray-900">Simple, Transparent Pricing</h2>
            <h4 className="text-1xl text-center text-gray-900">Start with 3 free posts. No credit card required.</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {/* Free Trial */}
              <div className="group bg-white hover:bg-blue-600 p-8 rounded-xl shadow-md border border-gray-200 transition-all duration-300 hover:shadow-xl relative">
                <div className="space-y-6">
                  <div className="text-center">
                    <h4 className="text-xl font-bold text-gray-900 group-hover:text-white">Free Trial</h4>
                    <p className="text-gray-500 mt-2 group-hover:text-blue-100">Try before you commit</p>
                    <p className="text-3xl font-bold text-gray-900 mt-4 group-hover:text-white">3 Credits</p>
                  </div>
                  <ul className="space-y-3">
                    <li className="flex items-center text-gray-600 group-hover:text-white">
                      <span className="mr-2 text-blue-600 group-hover:text-white">✓</span>
                      3 free posts (credits)
                    </li>
                    <li className="flex items-center text-gray-600 group-hover:text-white">
                      <span className="mr-2 text-blue-600 group-hover:text-white">✓</span>
                      Access to 3 analysis modes
                    </li>
                    <li className="flex items-center text-gray-600 group-hover:text-white">
                      <span className="mr-2 text-blue-600 group-hover:text-white">✓</span>
                      Credits never expire
                    </li>
                    <li className="flex items-center text-gray-600 group-hover:text-white">
                      <span className="mr-2 text-blue-600 group-hover:text-white">✓</span>
                      Full feature access
                    </li>
                  </ul>
                  <SignUpButton 
                    variant="pricing"
                    className="group-hover:bg-white group-hover:text-blue-600"
                  />
                </div>
              </div>

              {/* Basic Plan */}
              <div className="group bg-white hover:bg-blue-600 p-8 rounded-xl shadow-md border border-gray-200 transition-all duration-300 hover:shadow-xl relative">
                <div className="space-y-6">
                  <div className="text-center">
                    <h4 className="text-xl font-bold text-gray-900 group-hover:text-white">Basic</h4>
                    <p className="text-gray-500 mt-2 group-hover:text-blue-100">For individual professionals</p>
                    <p className="text-3xl font-bold text-gray-900 mt-4 group-hover:text-white">$9.99<span className="text-base font-normal text-gray-500 group-hover:text-blue-100">/mo</span></p>
                  </div>
                  <ul className="space-y-3">
                    <li className="flex items-center text-gray-600 group-hover:text-white">
                      <span className="mr-2 text-blue-600 group-hover:text-white">✓</span>
                      50 posts per month
                    </li>
                    <li className="flex items-center text-gray-600 group-hover:text-white">
                      <span className="mr-2 text-blue-600 group-hover:text-white">✓</span>
                      ~$0.20 per post
                    </li>
                    <li className="flex items-center text-gray-600 group-hover:text-white">
                      <span className="mr-2 text-blue-600 group-hover:text-white">✓</span>
                      All analysis modes
                    </li>
                    <li className="flex items-center text-gray-600 group-hover:text-white">
                      <span className="mr-2 text-blue-600 group-hover:text-white">✓</span>
                      Priority support
                    </li>
                  </ul>
                  <SignUpButton 
                    variant="pricing"
                    className="group-hover:bg-white group-hover:text-blue-600"
                  />
                </div>
              </div>

              {/* Pro Plan */}
              <div className="group bg-white hover:bg-blue-600 p-8 rounded-xl shadow-md border-2 border-blue-200 transition-all duration-300 hover:shadow-xl relative">
                <div className="absolute -top-3 right-6 bg-blue-600 text-white text-sm px-3 py-1 rounded-full group-hover:bg-white group-hover:text-blue-600">
                  Most Popular
                </div>
                <div className="space-y-6">
                  <div className="text-center">
                    <h4 className="text-xl font-bold text-gray-900 group-hover:text-white">Pro</h4>
                    <p className="text-gray-500 mt-2 group-hover:text-blue-100">For power users</p>
                    <p className="text-3xl font-bold text-gray-900 mt-4 group-hover:text-white">$19.99<span className="text-base font-normal text-gray-500 group-hover:text-blue-100">/mo</span></p>
                  </div>
                  <ul className="space-y-3">
                    <li className="flex items-center text-gray-600 group-hover:text-white">
                      <span className="mr-2 text-blue-600 group-hover:text-white">✓</span>
                      Unlimited posts
                    </li>
                    <li className="flex items-center text-gray-600 group-hover:text-white">
                      <span className="mr-2 text-blue-600 group-hover:text-white">✓</span>
                      All analysis modes
                    </li>
                    <li className="flex items-center text-gray-600 group-hover:text-white">
                      <span className="mr-2 text-blue-600 group-hover:text-white">✓</span>
                      Priority support
                    </li>
                    <li className="flex items-center text-gray-600 group-hover:text-white">
                      <span className="mr-2 text-blue-600 group-hover:text-white">✓</span>
                      Early access to new features
                    </li>
                  </ul>
                  <SignUpButton 
                    variant="pricing"
                    className="group-hover:bg-white group-hover:text-blue-600"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="text-red-600 mt-2">
              {error}
            </div>
          )}

          {/* Subscription Modal */}
          <SubscriptionModal
            isOpen={showSubscribeModal}
            onClose={() => setShowSubscribeModal(false)}
          />

          <button
            onClick={handleSignOut}
            className="text-gray-600 hover:text-gray-900"
          >
            Sign Out
          </button>
        </div>
      </div>
    </div>
  );
}
